import React, { useState } from 'react';
import Modal from './Modal';
import { FormInput } from './FormInput';
import { FormSelect } from './FormSelect';
import { FormTextArea } from './FormTextArea';
import { PhoneNumberInput } from './PhoneNumberInput';
import { FormSubmitButton } from './FormSubmitButton';
import Button from './Button';
import BranchSelector from './BranchSelector';
import styles from './ApplyModal.module.css';

const ApplyModal = ({ isOpen, onClose, appliedForRole, programInterest, onSubmit }) => {
  // Contact Information
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Program Interest Fields
  const [ageGroup, setAgeGroup] = useState('');
  const [experience, setExperience] = useState('');
  const [preferredLocation, setPreferredLocation] = useState('');
  const [notes, setNotes] = useState('');
  
  // Player Application Fields
  const [selectedProgram, setSelectedProgram] = useState('');
  const [childAge, setChildAge] = useState('');
  
  // Branch Application Fields
  const [selectedBranch, setSelectedBranch] = useState('');
  
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      email: email.trim(),
      phone: phone.trim() || null,
      applied_for_role: appliedForRole,
      program_interest: programInterest || null,
      applied_for_branch: selectedBranch || null,
    };

    try {
      if (onSubmit) await onSubmit(payload);

      // Reset form
      setEmail('');
      setPhone('');
      setAgeGroup('');
      setExperience('');
      setPreferredLocation('');
      setNotes('');
      setSelectedProgram('');
      setChildAge('');
      setSelectedBranch('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Unable to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic title based on role and program
  let title = 'Apply';
  let interestValue = '';
  let description = 'Thank you for your interest. Please submit your details below and our team will be in touch with you shortly.';

  if (programInterest) {
    title = 'Program Interest';
    interestValue = programInterest;
    description = 'Great choice! Provide your contact information and we\'ll reach out to discuss enrollment.';
  } else if (appliedForRole === 'parent') {
    title = 'Apply as Player';
    interestValue = 'Player';
    description = 'Ready to join? Browse our programs to find the perfect fit for your child.';
  } else if (appliedForRole === 'coach') {
    title = 'Apply as Coach';
    interestValue = 'Coach';
    description = 'We\'d love to have you join our coaching team. Share a bit about your experience and coaching philosophy.';
  } else if (appliedForRole === 'branch_admin') {
    title = 'Start a Chapter';
    interestValue = 'Chapter Director';
    description = 'Launch a successful soccer program in your community. Tell us about your vision and let\'s discuss how we can support you.';
  }

  return (
    <Modal isOpen={!!isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Description */}
        <div className={styles.description}>
          {description}
        </div>

        {/* SECTION 1: Basic Interest Information */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Your Interest</h3>
          
          <FormInput
            label="Interest"
            name="interest"
            value={interestValue}
            readOnly
            disabled
            className={styles.disabledField}
          />
        </div>

        {/* SECTION 2: Program Interest - Just Contact Info (show when programInterest is set) */}
        {programInterest && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Program Details</h3>
            <p className={styles.sectionHelp}>
              Thank you for your interest in this program. We'll reach out to discuss enrollment options and answer any questions.
            </p>
          </div>
        )}

        {/* SECTION 2: Player Application - Direct to Programs (show when applying as player without program) */}
        {appliedForRole === 'parent' && !programInterest && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Browse Our Programs</h3>
            <p className={styles.sectionHelp}>
              To apply as a player, please browse our programs and click "Learn More" on the program you're interested in. This will let us know your preferences and we'll get back to you with enrollment details.
            </p>
            
            <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
              <Button
                onClick={() => {
                  window.location.href = '/programs';
                }}
                variant="primary"
                style={{ width: '100%' }}
              >
                Take Me to Programs Page →
              </Button>
            </div>
          </div>
        )}

        {/* SECTION 4: Branch Selection (show for branch_admin role) */}
        {appliedForRole === 'branch_admin' && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Chapter Location</h3>
            <p className={styles.sectionHelp}>
              Select the location where you'd like to start a chapter, or leave blank if you have a new location in mind.
            </p>

            <BranchSelector
              label="Target Location"
              name="branch_id"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              required={false}
              placeholder="Select a location (optional)..."
            />
          </div>
        )}

        {/* SECTION 5: Contact Information (always shown) */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Contact Information</h3>
          <p className={styles.sectionHelp}>
            How can we reach you?
          </p>

          <FormInput
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
          />

          <PhoneNumberInput
            label="Phone Number"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. +1 555-555-5555"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <FormSubmitButton label="Submit Application" isLoading={isSubmitting} />
        </div>
      </form>
    </Modal>
  );
};

export default ApplyModal;
